import { b } from "@/b";
import {
  CURRENCY_FRAGMENT,
  CurrencyRenderer,
} from "@/components/CurrencyRenderer";
import { MEDIA_FRAGMENT, ResponsiveImage } from "@/components/ResponsiveImage";
import { Link } from "@chakra-ui/next-js";
import { Flex, Box, Text, ResponsiveValue, BoxProps } from "@chakra-ui/react";
import { FragmentData } from "@gqlb/core";
import { useRouter } from "next/router";
import React from "react";

export const ProductCard_Fragment = b.fragment(
  "ProductCard",
  "Product",
  (b) => [
    //
    b.id(),
    b.name(),
    b.slug(),
    b.price((b) => [b.__fragment(CURRENCY_FRAGMENT)]),
    b.availability((b) => [b.availableFrom((b) => [b.iso8601()])]),
    b.vipPrice((b) => [b.__fragment(CURRENCY_FRAGMENT)]),
    b.viewerPrice((b) => [b.__fragment(CURRENCY_FRAGMENT)]),
    b.images((b) => [b.__fragment(MEDIA_FRAGMENT)]),
  ]
);

export const ProductCard: React.FC<{
  data: FragmentData<typeof ProductCard_Fragment>;
  height?: BoxProps["height"];
  callout?: string;
}> = ({
  data: p,
  height = {
    base: "32",
    sm: "40",
    md: "48",
    lg: "64",
  },
  callout,
}) => {
  return (
    <Link
      key={p.id}
      alignItems="center"
      data-group
      w="full"
      flexDir="column"
      cursor="pointer"
      href={`/product/${p.slug}`}
      textDecor="none"
      _hover={{
        textDecoration: "none",
      }}
      pos="relative"
    >
      <Box
        w="full"
        h={height}
        transition={{
          base: "none",
          md: "transform 0.3s ease-in-out",
        }}
        _groupHover={{
          transform: { base: "none", md: "translateY(-8px)" },
        }}
        pos="relative"
        backgroundColor="#f5f5f5"
      >
        <Box
          pos="absolute"
          left="35%"
          bottom="-12px"
          right="0"
          top="0"
          backgroundColor="#8DEBCF"
          transition={{
            base: "none",
            md: "left 0.3s ease-in-out",
          }}
          _groupHover={{
            left: {
              base: "35%",
              md: "0",
            },
          }}
        />
        {p.images.length > 0 ? (
          <>
            <ResponsiveImage
              image={p.images[0]}
              objectFit="cover"
              pos="absolute"
              inset="0"
            />
            {p.images[1] && (
              <ResponsiveImage
                pos="absolute"
                inset="0"
                image={p.images[1]}
                objectFit="cover"
                opacity={0}
                transition={{
                  base: "none",
                  md: "opacity 0.3s ease-in-out",
                }}
                _groupHover={{
                  opacity: {
                    base: 0,
                    md: 1,
                  },
                }}
              />
            )}
          </>
        ) : null}
      </Box>
      <Box mt="6" w="full">
        <Text
          textAlign="left"
          fontSize={{
            base: "xs",
            md: "xl",
          }}
          fontWeight="bold"
          color="#191919"
          textTransform="uppercase"
        >
          {p.name}
        </Text>
        {p.viewerPrice.value > 0 && (
          <Text
            textAlign="left"
            fontSize={{
              base: "xs",
              md: "lg",
            }}
            fontWeight="normal"
            color="#6F7171"
          >
            {p.viewerPrice.value !== p.price.value ? (
              <Text as="span" textDecoration="line-through" mr="2">
                <CurrencyRenderer data={p.price} />
              </Text>
            ) : null}
            <CurrencyRenderer data={p.viewerPrice} />
          </Text>
        )}
      </Box>
      {callout && (
        <Box
          textAlign="left"
          fontSize={{
            base: "xs",
            md: "sm",
          }}
          bg="#8DEBCF"
          pos="absolute"
          top="0"
          left="0"
          display="flex"
          justifyContent="center"
          alignItems="center"
          textTransform="uppercase"
          fontWeight="bold"
          rounded="full"
          transform="translate(-10%, -50%)"
          py="1"
          px={{
            base: "2",
            md: "4",
          }}
        >
          {callout}
        </Box>
      )}
    </Link>
  );
};
